/* ws-cart.js — THE ONE CART WRITER (2026-09-11 cart-architecture fix).
 *
 * Root cause of the "random" cart bugs (proven with live race tests, see THEME-NOTES.md
 * § Cart architecture): the theme had FOUR independent writers hitting the Shopify cart with
 * no coordination — the product form (/cart/add), the quantity/remove controls (/cart/change,
 * addressed by LINE POSITION), the cart-drawer add-on rows (/cart/add.js) and the A/B arm
 * stamp (/cart/update.js, fired 1.5 s after every page load). Two writes in flight at once =
 * a lost update (a concurrent add + attribute stamp returned an EMPTY cart 1 run in 3) or a
 * position that no longer exists ("line parameter is invalid", or the WRONG line changed).
 *
 * This file makes every cart write go through one FIFO queue, at the network layer, so vendor
 * code (the minified product-form.js) is covered without being edited:
 *   - window.fetch is wrapped: any same-origin POST to /cart, /cart/add, /cart/change,
 *     /cart/update, /cart/clear (with or without .js) waits for the previous cart write to finish.
 *   - <html class="ws-cart-busy"> is set while a write is in flight (CSS disables the drawer's
 *     add-on buttons and quantity controls so a second tap cannot race the first).
 *   - WSCart.* helpers give our own code JSON-safe requests (a Shopify challenge page or a 429
 *     comes back as a plain "try again" error, never a raw parser message) and KEY-based line
 *     changes (a line item key never goes stale; a position does).
 * Load this before every other script that can touch the cart (layout/theme.liquid, head).
 */
(function () {
  'use strict';
  if (window.WSCart) return;

  var WRITE_RE = /^\/cart(\/add|\/change|\/update|\/clear)?(\.js)?$/;
  var origFetch = window.fetch.bind(window);
  var chain = Promise.resolve();
  var inflight = 0;

  function isCartWrite(input, init) {
    var url = typeof input === 'string' ? input : (input && input.url) || '';
    var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
    if (method !== 'POST') return false;
    try {
      var u = new URL(url, location.origin);
      return u.origin === location.origin && WRITE_RE.test(u.pathname);
    } catch (e) { return false; }
  }

  function setBusy(on) {
    var root = document.documentElement;
    if (on) root.classList.add('ws-cart-busy'); else root.classList.remove('ws-cart-busy');
  }

  window.fetch = function (input, init) {
    if (!isCartWrite(input, init)) return origFetch(input, init);
    var run = function () {
      inflight++; setBusy(true);
      return origFetch(input, init).finally(function () {
        inflight--; if (inflight <= 0) { inflight = 0; setBusy(false); }
      });
    };
    var p = chain.then(run, run);
    chain = p.catch(function () {});
    return p;
  };

  var GENERIC = 'Sorry, that did not go through. Please try again.';

  function request(path, body) {
    return window.fetch(path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    }).then(function (r) {
      return r.text().then(function (t) {
        var j = null;
        try { j = JSON.parse(t); } catch (e) { j = null; }
        if (!j) { var err = new Error(GENERIC); err.code = 'non_json'; err.status = r.status; throw err; }
        if (!r.ok || j.status) { var e2 = new Error(j.description || j.message || GENERIC); e2.code = 'cart_error'; e2.status = r.status; throw e2; }
        return j;
      });
    });
  }

  function sectionsParam(sections) {
    return Array.isArray(sections) ? sections.join(',') : sections;
  }

  window.WSCart = {
    /** add([{id, quantity}], ['cart-drawer','cart-icon-bubble']) */
    add: function (items, sections) {
      return request('/cart/add.js', { items: items, sections: sectionsParam(sections), sections_url: location.pathname });
    },
    /** change({ key } | { line }, quantity, sections) — key wins; position is the fallback */
    change: function (ref, quantity, sections) {
      var body = { quantity: quantity, sections: sectionsParam(sections), sections_url: location.pathname };
      if (ref && ref.key) body.id = ref.key; else body.line = ref.line;
      return request('/cart/change.js', body);
    },
    attributes: function (attrs) { return request('/cart/update.js', { attributes: attrs }); },
    /** sections(['cart-drawer','cart-icon-bubble']) → { 'cart-drawer': html, ... } (a READ, not queued) */
    sections: function (sections) {
      return origFetch('/cart?sections=' + encodeURIComponent(sectionsParam(sections))) // no Accept header: with it, /cart answers as the cart JSON endpoint and drops the sections
        .then(function (r) { return r.json(); });
    },
    busy: function () { return inflight > 0; },
    GENERIC_ERROR: GENERIC,
  };
})();
